/* eslint-disable react/no-unknown-property */
import React from 'react';
import { useParams } from 'react-router-dom';
import {
  ActionIconDropdown,
  Card,
  DetailItemList,
  Dropdown,
  FontWeight, HStack, ICON_LIST_BOX_OUTLINE, Stack, Text,
  UserPreview,
} from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import RightColumnPageLayout from '../../layouts/right-column-page-layout/RightColumnPageLayout';
import useTask from '../hooks/use-task/useTask';
import { raise } from '../../utils';
import RightColumnPageSkeleton from '../../components/page-skeleton/RightColumnPageSkeleton';
import GenericErrorPage from '../../pages/errors/generic/GenericErrorPage';
import TaskPriorityBadge from '../components/task-priority-badge/TaskPriorityBadge';
import TaskAvatar from '../components/task-avatar/TaskAvatar';
import { setVerticalEllipsis } from '../../utils/styles/verticalEllipsis.style';
import CopyLinkAction from '../../components/copy-link-action/CopyLinkAction';
import UpsertTaskAction from '../containers/upsert-task-action/UpsertTaskAction';
import useRBAC from '../../hooks/use-rbac/useRBAC';
import { ReferenceType, TaskStatus } from '../../domain/types';
import ViewTaskPopoverPreview from '../containers/view-task-popover-preview/ViewTaskPopoverPreview';
import ViewPropertyPreview from '../../property/containers/view-property-preview/ViewPropertyPreview';
import UpdateTaskStatus from '../containers/update-task-status/UpdateTaskStatus';
import { contentEditorUsers } from '../constants';
import useCurrentUrl from '../../hooks/use-current-url/useCurrentUrl';
import { goToDetailPage } from '../../utils/navigate/utils';

export const LOAD_TASK_ERROR_MESSAGE = "Non è stato possibile caricare le informazioni dell'attività";

const ViewTaskPage: React.FC = () => {
  const { id } = useParams();
  const taskId = id ?? raise('Missing task id');

  const currentUrl = useCurrentUrl();

  const { userIsAdmin, user } = useRBAC();

  const {
    data: task, isLoading: isTaskLoading, error: taskError, mutate: mutateTask,
  } = useTask(taskId);

  const canEditTask = React.useMemo(() => task
    && [TaskStatus.TODO, TaskStatus.IN_PROGRESS].includes(task.status)
    && (userIsAdmin || user.email === task?.reporter || user.email === task?.assignee), [task, user.email, userIsAdmin]);

  const assignee = React.useMemo(
    () => (task?.assignee ? contentEditorUsers.find((u) => u.email === task.assignee) : undefined),
    [task],
  );

  if (taskError) {
    return <GenericErrorPage title={LOAD_TASK_ERROR_MESSAGE} />;
  }

  if (!task || isTaskLoading) {
    return <RightColumnPageSkeleton />;
  }

  return (
    <RightColumnPageLayout
      title={(
        <ViewTaskPopoverPreview
          task={task}
          trigger={(
            <HStack
              gap={200}
              wrap="nowrap"
            >
              <TaskAvatar size="M" />
              <div>
                <Text.H4
                  aria-label="Titolo attività"
                  fontWeight={FontWeight.REGULAR}
                  css={setVerticalEllipsis()}
                >
                  {task.title}
                </Text.H4>
                <TaskPriorityBadge showIcon size="XS" task={task} />
              </div>
            </HStack>
          )}
        />
      )}
      actions={(
        <ActionIconDropdown
          label="Azioni sull'attività"
        >
          <CopyLinkAction link={currentUrl}>
            {({ onCopy }) => (
              <Dropdown.Option
                label="Copia riferimento"
                aria-label="Copia riferimento all'attività"
                onClick={onCopy}
              />
            )}
          </CopyLinkAction>

          {canEditTask && (
            <UpsertTaskAction
              task={task}
              onSuccess={() => mutateTask()}
            >
              {({ upsert }) => (
                <Dropdown.Option
                  label="Modifica attività"
                  aria-label="Modifica dettagli dell'attività"
                  onClick={upsert}
                />
              )}
            </UpsertTaskAction>
          )}
        </ActionIconDropdown>
      )}
      sidePrimaryContent={(
        <Card>
          <Card.Header isSecondary>
            <UpdateTaskStatus
              task={task}
              emphasis="high"
              onSuccess={() => mutateTask()}
              showIcon
            />
          </Card.Header>
          <Card.Content>
            <DetailItemList>
              {task.createdAt && (
                <DetailItemList.Item label="Data creazione">
                  {formatDate(new Date(task.createdAt))}
                </DetailItemList.Item>
              )}
              <DetailItemList.Item label="Assegnato a" withoutVPadding>
                <UserPreview
                  size="S"
                  name={assignee ? assignee.name : 'Non assegnato'}
                />
              </DetailItemList.Item>
            </DetailItemList>
          </Card.Content>
        </Card>
      )}
      aria-label="Pagina task"
    >
      <Card>
        <Card.Header icon={{ path: ICON_LIST_BOX_OUTLINE }} title="Dettagli" />
        <Card.Content>
          <Stack gap={100}>
            <Text.Caption>Riferimento</Text.Caption>
            {task.reference?.type === ReferenceType.PROPERTY && (
              <ViewPropertyPreview
                propertyId={parseInt(task.reference.id, 10)}
                onClick={({ id: propertyId }) => goToDetailPage(`${propertyId}`, ReferenceType.PROPERTY)}
              />
            )}
          </Stack>
        </Card.Content>
        {task.description && (
          <Card.Content>
            <Stack gap={100}>
              <Text.Caption>Descrizione</Text.Caption>
              <Text.Body aria-label="Descrizione del task">{task.description}</Text.Body>
            </Stack>
          </Card.Content>
        )}
      </Card>
    </RightColumnPageLayout>
  );
};

export default ViewTaskPage;
